/**
 * https://github.com/bae-sh/tiptap-extension-resize-image/blob/main/lib/imageResize.ts
 * https://gist.github.com/slava-vishnyakov/16076dff1a77ddaca93c4bccd4ec4521?permalink_comment_id=5002176#gistcomment-5002176
 */

import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Component from './Component';
import getImageDropPastePlugin from './getImageDropPastePlugin';
import { IMAGE_HTML_ATTRS, IMAGE_NODE_NAME } from './constants';
import createRightClickMenuPlugin from '../../plugins/createRightClickMenuPlugin';
import { Dispatch, SetStateAction } from 'react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      insertImage: (file: File) => ReturnType;
    };
  }
}

type TOptions = {
  setIsImageMenuOpen?: Dispatch<SetStateAction<boolean>>;
};

const ImageNode = Node.create<TOptions>({
  name: IMAGE_NODE_NAME,
  inline: false,
  group: 'block',
  draggable: true,
  atom: true,

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
      setIsImageMenuOpen: undefined, // unavailable by default, provided by DocumentContext
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img[data-blob-name]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', HTMLAttributes];
  },

  addAttributes() {
    return {
      file: {
        default: null,
        rendered: false,
      },
      /**
       * Both blobName and docId are `null` when the image is first inserted (before upload).
       * So NOT using `attrs.docId ? { 'data-doc-id': attrs.docId } : {}` is done on purpose.
       */
      blobName: {
        default: null,
        parseHTML: (element) => element.getAttribute(IMAGE_HTML_ATTRS.BLOB_NAME),
        renderHTML: (attrs) => ({ [IMAGE_HTML_ATTRS.BLOB_NAME]: attrs.blobName }),
      },
      // needed for cross-doc copy (removed before save, see cleanForSave.helper.ts)
      docId: {
        default: null,
        parseHTML: (el) => el.getAttribute(IMAGE_HTML_ATTRS.DOC_ID),
        renderHTML: (attrs) => ({ [IMAGE_HTML_ATTRS.DOC_ID]: attrs.docId }),
      },
      textAlign: {
        // data-align is set to `left` by default on both backend and frontend
        default: 'left',
        parseHTML: (element) => element.getAttribute(IMAGE_HTML_ATTRS.TEXT_ALIGN) ?? (element.style.textAlign || null),
        renderHTML: (attrs) => ({ [IMAGE_HTML_ATTRS.TEXT_ALIGN]: attrs.textAlign }),
      },
      wrap: {
        default: null,
        parseHTML: (element) => element.getAttribute('wrap') === 'true',
        renderHTML: (attrs) => (attrs.wrap ? { wrap: attrs.wrap ? 'true' : 'false' } : {}),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width') || 'auto',
        renderHTML: (attrs) => ({ width: attrs.width || 'auto' }),
      },
      alt: {
        default: null, // Used to show Word import error!
        parseHTML: (element) => element.getAttribute('alt') || null,
        renderHTML: (attrs) => ({ alt: attrs.alt }),
      },
    };
  },
  addCommands() {
    return {
      // ...this.parent?.(),
      insertImage:
        (file) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { file },
          });
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
  addProseMirrorPlugins() {
    return [
      getImageDropPastePlugin(),
      createRightClickMenuPlugin({
        kind: 'image',
        onOpen: () => this.options?.setIsImageMenuOpen?.(true),
        onClose: () => this.options?.setIsImageMenuOpen?.(false),
      }),
    ];
  },
});

export default ImageNode;
